import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buyerAllocationApi, type SaveBuyerAllocationPayload } from "@/api/buyers";
import { buyerAllocationKeys } from "./buyerKeys";

/**
 * The allocation policy and the entitlement it produces (PR 2b), as hooks.
 *
 * <p>Two readers share these: the allocation policy page (every standing the
 * caller dispatches, plus the global row) and a team's ALLOCATION tab (one
 * team's standings, plus that team's tier). One definition of each read, so the
 * two surfaces cannot key the same request two ways.</p>
 *
 * <p>🔴 <b>Every mutation invalidates `buyerAllocationKeys.all`.</b> A tier is
 * an INPUT to every entitlement — entitlement is recomputed on read, never
 * stored — so writing a team's tier moves each of its members' standings, the
 * policy page's tier list, and every specialist's own widget. `all` is the only
 * prefix that reaches all three.</p>
 */

/** The policy: the global row, the per-team tiers and the per-specialist
 *  overrides. Gated `Buyers.ManageAllocation` server-side, so a caller without
 *  it must pass `enabled: false` rather than fetch a 403. */
export function useBuyerAllocationSettings(enabled = true) {
  return useQuery({
    queryKey: buyerAllocationKeys.settings(),
    queryFn: () => buyerAllocationApi.settings(),
    enabled,
  });
}

/**
 * Standings — every specialist the caller dispatches, or ONE team's roster
 * when `buyerTeamId` is given.
 *
 * <p>The team-scoped key sits under the unscoped one, so an assignment's
 * `entitlements()`-prefix invalidation still reaches it.</p>
 */
export function useBuyerEntitlements(buyerTeamId?: string, enabled = true) {
  return useQuery({
    queryKey: buyerAllocationKeys.entitlements(buyerTeamId),
    queryFn: () => buyerAllocationApi.entitlements(buyerTeamId ? { buyerTeamId } : undefined),
    enabled,
  });
}

/** Author ONE team's tier. An all-null body reverts the team to the global
 *  policy — the server's spelling of "inherit", which is why the editor sends
 *  `null` and never `0` for a cleared field. */
export function useSaveTeamTier(buyerTeamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveBuyerAllocationPayload) =>
      buyerAllocationApi.saveTeamTier(buyerTeamId, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: buyerAllocationKeys.all });
    },
  });
}

/** Delete ONE team's tier so the team follows the global policy again. */
export function useClearTeamTier(buyerTeamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => buyerAllocationApi.clearTeamTier(buyerTeamId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: buyerAllocationKeys.all });
    },
  });
}
